import * as React from "react";
import {
  Typography,
  useTheme,
  Popper,
  Paper,
  Box,
  Tooltip,
  MenuItem,
  ClickAwayListener,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  add,
  addHours,
  format,
  isAfter,
  isBefore,
  isPast,
  isValid,
  isWithinInterval,
  set,
} from "date-fns";
import { DigitalClock } from "@mui/x-date-pickers/DigitalClock";
import { AppContext } from "../../contextProviders/AppContext";
import { useContext } from "react";
import { centsToLocalString } from "../../shared/money";
import { translation } from "../Dictionary";
import { FcClock } from "react-icons/fc";
import Close from "@mui/icons-material/Close";
export default function TimePickerComponent({
  title,
  selectedPicker,
  setSelectedPicker,
  type,
}) {
  const theme = useTheme();
  const {
    locale,
    selectedDateRange,
    setSelectedDateRange,
    selectedCompany,
    mobileViewPort,
    businessHourDates,
    FEATURE_DISABLE_OFFHOUR_RETURN,
  } = useContext(AppContext);

  const timeValue = React.useMemo(() => {
    if (type === "startTime") {
      return selectedDateRange[0];
    }
    if (type === "returnTime") {
      return selectedDateRange[1];
    }
  }, [type, selectedDateRange]);

  const [anchorPopper, setAnchorPopper] = React.useState(null);

  const ref = React.useRef(null);
  React.useEffect(() => {
    setAnchorPopper(ref.current);
  }, []);
  const shouldDisableTime = (value) => {
    const deliveryBufferTime = addHours(
      new Date(),
      selectedCompany?.deliveryBufferHours ?? 0
    );

    if (isPast(value) && type === "startTime") {
      return true;
    }
    if (isBefore(value, deliveryBufferTime)) {
      return true;
    }

    if (type === "returnTime") {
      if (
        isBefore(value, selectedDateRange[0]) ||
        isBefore(value, addHours(selectedDateRange[0], 1)) // force minimum reservation duration to 1h
      ) {
        return true;
      }

      // 👇 NEW LOGIC: Disable times outside business hours if the feature is active
      if (FEATURE_DISABLE_OFFHOUR_RETURN) {
        // You'll need to pass or calculate the end day business hours here
        const businessHours = businessHourDates(
          selectedDateRange,
          selectedCompany
        );
        const endDayOpenTime = businessHours?.end?.openingTime;
        const endDayCloseTime = businessHours?.end?.closingTime;

        if (endDayOpenTime && endDayCloseTime) {
          // Disable if the time on the picker is before opening or after closing
          if (
            isBefore(value, endDayOpenTime) ||
            isAfter(value, endDayCloseTime)
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };
  const handleChangeTime = (newValue) => {
    if (type === "startTime") {
      setSelectedDateRange((prevState) => [
        newValue,
        isBefore(prevState[1], newValue)
          ? add(newValue, { hours: 1 })
          : prevState[1],
      ]);
    }
    if (type === "returnTime") {
      setSelectedDateRange((prevState) => [prevState[0], newValue]);
    }
  };

  const offHourAvailabilityFee = React.useMemo(() => {
    if (
      selectedCompany?.offHourAvailabilityFee === 0 ||
      !selectedCompany?.offHourAvailabilityFee
    ) {
      return false;
    }
    return selectedCompany?.offHourAvailabilityFee;
  }, [selectedCompany]);

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        borderRadius: "10px",
        bgcolor: selectedPicker === type ? "white" : "transparent",
        boxShadow: selectedPicker === type ? theme.shadows[2] : "transparent",
        height: "54px",
        justifyContent: "center",
        alignItems: "center",
        transition: "ease-in-out 250ms",
        borderStyle: "solid",
        borderWidth: 1,
        cursor: "pointer",
        minWidth: "55px",
        paddingX: "15px",
        "&:hover": {
          bgcolor: "rgba(0,0,0,0.05)",
          boxShadow: theme.shadows[2],
        },
        borderColor: mobileViewPort ? "rgba(0,0,0,0.15)" : "transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={() => {
          if (
            (type === "startTime" && !selectedDateRange[0]) ||
            (type === "returnTime" && !selectedDateRange[1])
          ) {
            return null;
          }

          setSelectedPicker(type);
        }}
      >
        <Typography
          color={theme.palette.primary.mainText}
          sx={{ wordWrap: "break-word", textAlign: "center" }}
        >
          {title}
        </Typography>
        <Typography color={theme.palette.primary.mainText}>
          {isValid(timeValue)
            ? format(new Date(timeValue), "HH:mm", {
                locale: locale,
              })
            : ""}
        </Typography>
      </Box>
      <DesktopTimePicker
        offHourAvailabilityFee={offHourAvailabilityFee}
        anchorPopper={anchorPopper}
        selectedPicker={selectedPicker}
        type={type}
        setSelectedPicker={setSelectedPicker}
        shouldDisableTime={shouldDisableTime}
        timeValue={timeValue}
        handleChangeTime={handleChangeTime}
        businessHourDates={businessHourDates}
        selectedDateRange={selectedDateRange}
      />
      <MobileTimePicker
        offHourAvailabilityFee={offHourAvailabilityFee}
        anchorPopper={anchorPopper}
        selectedPicker={selectedPicker}
        type={type}
        setSelectedPicker={setSelectedPicker}
        shouldDisableTime={shouldDisableTime}
        timeValue={timeValue}
        handleChangeTime={handleChangeTime}
        businessHourDates={businessHourDates}
        selectedDateRange={selectedDateRange}
        title={title}
      />
    </Box>
  );
}

const MobileTimePicker = (props) => {
  const {
    businessHourDates,
    handleChangeTime,
    offHourAvailabilityFee,
    selectedDateRange,
    selectedPicker,
    setSelectedPicker,
    shouldDisableTime,
    timeValue,
    type,
  } = props;
  const { mobileViewPort, language } = useContext(AppContext);

  if (mobileViewPort)
    return (
      <Dialog
        open={selectedPicker === type}
        sx={{
          marginTop: "65px",

          zIndex: 1500,
        }}
        fullScreen
      >
        <Box
          sx={{
            margin: "10px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <FcClock style={{ fontSize: "32px" }} />
              <Typography
                sx={{
                  fontSize: "18px",
                  fontFamily: "Sofia Pro",
                  marginLeft: "15px",
                }}
              >
                {type === "startTime" &&
                  translation.get(language, "Timepicker starttime title")}
                {type === "returnTime" &&
                  translation.get(language, "Timepicker returntime title")}
              </Typography>
            </Box>
            <IconButton onClick={() => setSelectedPicker(null)}>
              <Close />
            </IconButton>
          </Box>
          {offHourAvailabilityFee && type === "startTime" && (
            <Typography variant="caption">
              Tähdellä merkittyihin aikoihin on sisältyy{" "}
              <b> {centsToLocalString(offHourAvailabilityFee)}</b> euron
              päivystyslisä.
            </Typography>
          )}
        </Box>
        <DigitalClock
          sx={{
            "&.MuiDigitalClock-root": {
              minHeight: "85%",
            },
          }}
          skipDisabled
          shouldDisableTime={shouldDisableTime}
          value={timeValue}
          onChange={(newValue) => {
            handleChangeTime(newValue);
            setSelectedPicker(null);
          }}
          //   minTime={dayjs('2022-04-17T09:00')}
          timeStep={15}
          slots={{ digitalClockItem: CustomMenuItemMobile }}
          slotProps={{
            digitalClockItem: {
              clocktype: type,
              openhours: businessHourDates(selectedDateRange),
              custompickertype: type,
            },
          }}
        />
      </Dialog>
    );
};

const DesktopTimePicker = (props) => {
  const {
    offHourAvailabilityFee,
    anchorPopper,
    selectedPicker,
    type,
    setSelectedPicker,
    shouldDisableTime,
    timeValue,
    handleChangeTime,
    businessHourDates,
    selectedDateRange,
  } = props;
  const { mobileViewPort } = useContext(AppContext);

  if (!mobileViewPort)
    return (
      <Popper
        id={"textIdPopper"}
        open={selectedPicker === type}
        anchorEl={anchorPopper}
        placement="bottom"
        sx={{
          offset: "25px",
          zIndex: 1500,
        }}
      >
        <ClickAwayListener
          onClickAway={() => {
            setSelectedPicker(null);
          }}
        >
          <Tooltip
            title={
              offHourAvailabilityFee &&
              type === "startTime" &&
              `Tähdellä merkittyihin aikoihin sisältyy ${centsToLocalString(
                offHourAvailabilityFee
              )} euron päivystyslisä.`
            }
          >
            <Paper
              elevation={3}
              sx={{
                borderRadius: "20px",
                marginTop: "12px",
                overflow: "hidden",
                padding: "10px",
                width: "100px",
              }}
            >
              <Box
                sx={{
                  maxHeight: "150px",
                  overflow: "auto",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <DigitalClock
                  skipDisabled
                  shouldDisableTime={shouldDisableTime}
                  value={timeValue}
                  onChange={(newValue) => {
                    handleChangeTime(newValue);
                    if (selectedPicker === "startTime") {
                      setSelectedPicker("returnTime");
                    }
                    if (selectedPicker === "returnTime") {
                      setSelectedPicker(null);
                    }
                  }}
                  //   minTime={dayjs('2022-04-17T09:00')}
                  timeStep={15}
                  slots={{ digitalClockItem: CustomMenuItem }}
                  slotProps={{
                    digitalClockItem: {
                      clocktype: type,
                      openhours: businessHourDates(selectedDateRange),
                      custompickertype: type,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Tooltip>
        </ClickAwayListener>
      </Popper>
    );
};

const CustomMenuItem = (props) => {
  const { selectedCompany, businessHourDates, selectedDateRange } =
    useContext(AppContext);
  const offHourAvailabilityFee =
    selectedCompany?.offHourAvailabilityFee === 0 ||
    selectedCompany?.offHourAvailabilityFee === null
      ? false
      : true;
  const isInRange =
    props.custompickertype === "startTime"
      ? isTimeInRange(
          props.children,
          businessHourDates(selectedDateRange)?.start,
          selectedDateRange[0]
        )
      : isTimeInRange(
          props.children,
          businessHourDates(selectedDateRange)?.end,
          selectedDateRange[1]
        );

  const byPicker = selectedCompany?.organizationId
    ? props.custompickertype === "startTime"
    : true;

  if (!offHourAvailabilityFee && !isInRange && byPicker) {
    return null;
  }

  return (
    <MenuItem
      {...props}
      sx={{
        fontWeight:
          !isTimeInRange(
            props.children,

            businessHourDates(selectedDateRange)?.start,

            selectedDateRange[0]
          ) &&
          offHourAvailabilityFee &&
          props.custompickertype === "startTime"
            ? 600
            : undefined,
      }}
    >
      {props.children}{" "}
      {offHourAvailabilityFee &&
        props.custompickertype === "startTime" &&
        !isTimeInRange(
          props.children,

          businessHourDates(selectedDateRange)?.start,

          selectedDateRange[0]
        ) &&
        "*"}
    </MenuItem>
  );
};

const CustomMenuItemMobile = (props) => {
  const { selectedCompany, businessHourDates, selectedDateRange } =
    useContext(AppContext);
  const offHourAvailabilityFee =
    selectedCompany?.offHourAvailabilityFee === 0 ||
    selectedCompany?.offHourAvailabilityFee === null
      ? false
      : true;
  const isInRange =
    props.custompickertype === "startTime"
      ? isTimeInRange(
          props.children,
          businessHourDates(selectedDateRange)?.start,
          selectedDateRange[0]
        )
      : isTimeInRange(
          props.children,
          businessHourDates(selectedDateRange)?.end,
          selectedDateRange[1]
        );

  const byPicker = selectedCompany?.organizationId
    ? props.custompickertype === "startTime"
    : true;

  if (!offHourAvailabilityFee && !isInRange && byPicker) {
    return null;
  }

  return (
    <MenuItem {...props} sx={{ borderBottom: "1px solid rgba(0,0,0,0.25)" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight:
              !isTimeInRange(
                props.children,
                businessHourDates(selectedDateRange)?.start,
                selectedDateRange[0]
              ) &&
              offHourAvailabilityFee &&
              props.custompickertype === "startTime"
                ? 600
                : undefined,
          }}
        >
          {props.children}{" "}
          {offHourAvailabilityFee &&
            props.custompickertype === "startTime" &&
            !isTimeInRange(
              props.children,
              businessHourDates(selectedDateRange)?.start,
              selectedDateRange[0]
            ) &&
            "*"}
        </Typography>
      </Box>
    </MenuItem>
  );
};

function isTimeInRange(children, openHours, date) {
  const time = set(date, {
    hours: children.split(":")[0],
    minutes: children.split(":")[1],
  });
  if (openHours?.openingTime && openHours?.closingTime) {
    return isWithinInterval(time, {
      start: openHours?.openingTime,
      end: openHours?.closingTime,
    });
  }
}
